import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    ClipboardText, Clock, CaretDown, CaretUp, GitBranch, ArrowsLeftRight,
    Link as LinkIcon, VideoCamera, Sparkle, Lightning,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { computeETA, fmtETA } from "@/lib/planTimeline";

/* PlanPanel renders the planner-authored treatment plan in a read-only,
   doctor-friendly summary. If no plan exists yet (planner hasn't filled it),
   it shows a clean empty state.

   When `embedded` is true (set when PlanPanel is rendered inside a parent
   "iteration card" — like in PlanIterationsStack), we skip our own
   header/wrapper chrome so we don't double-frame the content. The parent
   card already carries the title, version, and history. */
export default function PlanPanel({ caseId, plan, status, caseFiles = [], embedded = false }) {
    const [versions, setVersions] = useState(null);
    const [versionCount, setVersionCount] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    /* iter98.99dd — Collapsed by default per user instruction (all
       portals, all tabs). Parent can re-open via the chevron. */
    const [expanded, setExpanded] = useState(false);

    // Fetch version count once on mount (cheap, small array, cached client-side)
    useEffect(() => {
        if (!caseId || embedded) return;
        api.get(`/cases/${caseId}/plan-versions`)
            .then((r) => { setVersions(r.data); setVersionCount(r.data.length); })
            .catch(() => setVersionCount(0));
    }, [caseId, embedded]);

    const hasPlan = plan && Object.keys(plan).some((k) => !["updated_at", "updated_by", "updated_by_name"].includes(k) && plan[k] !== null && plan[k] !== "" && plan[k] !== undefined);

    if (!hasPlan) {
        // Empty state, only shows once we're past planning_started
        if (status === "draft" || status === "submitted") return null;
        return (
            <div className="border border-dashed border-border rounded-xl p-4 bg-secondary/20 text-center" data-testid="plan-panel-empty">
                <ClipboardText size={28} className="mx-auto text-muted-foreground/60 mb-2" />
                <div className="text-sm text-muted-foreground">
                    Planner hasn't filled the treatment plan yet.
                </div>
            </div>
        );
    }

    /* In embedded mode we render just the body (no card chrome, no header).
       The parent card already provides the visual frame + the version /
       attribution chrome. */
    if (embedded) {
        return (
            <div className="space-y-4 text-sm" data-testid="plan-panel-embedded">
                <PlanBody plan={plan} caseFiles={caseFiles} />
            </div>
        );
    }

    return (
        <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid="plan-panel">
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full px-4 py-3 flex items-center justify-between gap-2 bg-secondary/30 hover:bg-secondary/50 border-b border-border"
            >
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <ClipboardText size={16} weight="duotone" className="text-primary" />
                    Treatment plan
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium uppercase tracking-wide" data-testid="plan-version-badge">
                        v{Math.max(1, versionCount || 1)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setShowHistory(true); }} data-testid="plan-history-btn">
                        <GitBranch size={12} className="mr-1" /> History
                    </Button>
                    {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </div>
            </button>

            {expanded && (
                <div className="p-4">
                    <PlanBody plan={plan} caseFiles={caseFiles} />
                </div>
            )}

            <PlanHistoryDialog open={showHistory} onClose={() => setShowHistory(false)} versions={versions} />
        </div>
    );
}

/* PlanBody — the actual plan content (link, metrics, protocol, retention).
   Extracted so PlanPanel can render with or without its outer card chrome. */
function PlanBody({ plan, caseFiles }) {
    // Only show metric cards that actually have a value — otherwise we get
    // empty placeholder boxes that look broken.
    const hasUpper = plan.aligner_count_upper != null && plan.aligner_count_upper !== "";
    const hasLower = plan.aligner_count_lower != null && plan.aligner_count_lower !== "";
    const hasDuration = plan.estimated_duration_weeks != null && plan.estimated_duration_weeks !== "";
    const hasChange = plan.change_interval_days != null && plan.change_interval_days !== "";
    const anyMetric = hasUpper || hasLower || hasDuration || hasChange;

    return (
        <div className="space-y-4 text-sm">
            {/* 3rd-party plan deliverable (link or video) — the primary planner output.
                Theme-aligned cyan tint (not violet) and rendered as a clean inline link. */}
            {(plan.external_link || plan.video_file_id) && (
                <div className="rounded-xl border border-primary/20 bg-primary/[0.04] p-3 space-y-1.5">
                    {plan.external_link && (
                        <a href={plan.external_link} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 text-primary font-semibold hover:underline text-sm"
                           data-testid="plan-external-link-view"
                        >
                            <LinkIcon size={14} weight="bold" /> Open the 3D plan viewer
                        </a>
                    )}
                    {plan.video_file_id && (() => {
                        const v = caseFiles.find((f) => f.id === plan.video_file_id);
                        return v ? (
                            <a href={`${API}/files/${v.id}?auth=${localStorage.getItem("ca_token")}`}
                               target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-2 text-primary font-semibold hover:underline text-sm"
                               data-testid="plan-video-view"
                            >
                                <VideoCamera size={14} weight="bold" /> {v.name}
                            </a>
                        ) : null;
                    })()}
                </div>
            )}

            {/* Aligners & duration row — only show metric cards that have a value. */}
            {anyMetric && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {hasUpper    && <Metric label="Upper" value={plan.aligner_count_upper} suffix="aligners" />}
                    {hasLower    && <Metric label="Lower" value={plan.aligner_count_lower} suffix="aligners" />}
                    {hasDuration && <Metric label="Duration" value={plan.estimated_duration_weeks} suffix="weeks" />}
                    {hasChange   && <Metric label="Change every" value={plan.change_interval_days} suffix="days" />}
                </div>
            )}

            {/* iter97.38 — Phase B projected ETA chip. Mirrors the chip on
                the doctor self-plan sticky bar so both authoring surfaces
                share one timeline language. Only renders when we have
                enough inputs to compute (stages + wear). */}
            {(() => {
                const eta = computeETA(plan);
                if (!eta.has_min_inputs) return null;
                return (
                    <div
                        className={`rounded-lg border px-3 py-2 flex items-center gap-2 text-sm ${eta.eta
                            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                            : "border-amber-200 bg-amber-50/70 text-amber-900"}`}
                        data-testid="plan-panel-eta-chip"
                    >
                        <Lightning size={13} weight="fill" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Projected ETA</span>
                        <span className="font-semibold tabular-nums">{fmtETA(eta)}</span>
                        <span className="text-[11px] text-muted-foreground ml-auto">
                            {eta.total_treatment_days}d
                            {eta.total_pause_days > 0 && ` (incl. ${eta.total_pause_days}d pauses)`}
                        </span>
                    </div>
                );
            })()}

                    {/* Bite metrics */}
                    {(plan.initial_overjet_mm || plan.initial_overbite_mm || plan.final_overjet_mm || plan.final_overbite_mm) && (
                        <div className="border-t border-border pt-3">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">Bite metrics</div>
                            <div className="grid grid-cols-2 gap-3">
                                <BiteRow label="Overjet" initial={plan.initial_overjet_mm} final={plan.final_overjet_mm} />
                                <BiteRow label="Overbite" initial={plan.initial_overbite_mm} final={plan.final_overbite_mm} />
                            </div>
                            {plan.total_ipr_mm != null && plan.total_ipr_mm !== "" && (
                                <div className="text-xs mt-2"><span className="text-muted-foreground">Total IPR:</span> <span className="font-semibold">{plan.total_ipr_mm} mm</span></div>
                            )}
                        </div>
                    )}

                    {/* Protocol */}
                    {(plan.proposed_attachments || plan.proposed_ipr || plan.proposed_elastics) && (
                        <div className="border-t border-border pt-3 space-y-2">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Proposed protocol</div>
                            {plan.proposed_attachments && <Para label="Attachments" value={plan.proposed_attachments} />}
                            {plan.proposed_ipr && <Para label="IPR" value={plan.proposed_ipr} />}
                            {plan.proposed_elastics && <Para label="Elastics" value={plan.proposed_elastics} />}
                        </div>
                    )}

                    {plan.planner_notes && (
                        <div className="border-t border-border pt-3">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Planner notes</div>
                            <p className="text-xs text-foreground/80 whitespace-pre-wrap">{plan.planner_notes}</p>
                        </div>
                    )}

                    {/* C — Overcorrection */}
                    {plan.has_overcorrection && (
                        <div className="border-t border-border pt-3 space-y-1.5" data-testid="plan-readonly-overcorrection">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Overcorrection</div>
                            <div className="text-xs text-foreground/90 flex items-center gap-1.5">
                                <Sparkle size={11} weight="fill" className="text-primary" />
                                Plan includes overcorrection
                            </div>
                            {plan.overcorrection_notes && (
                                <p className="text-xs text-foreground/80 whitespace-pre-wrap">{plan.overcorrection_notes}</p>
                            )}
                        </div>
                    )}

                    {/* B — Auxiliaries & features */}
                    {(plan.aux_buttons || plan.aux_bite_ramps || plan.aux_mandibular_advancement
                        || plan.aux_precision_cuts || plan.aux_hooks || plan.aux_other_notes) && (
                        <div className="border-t border-border pt-3 space-y-1.5" data-testid="plan-readonly-auxiliaries">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Auxiliaries & features</div>
                            <div className="flex flex-wrap gap-1.5">
                                {plan.aux_buttons && <AuxChip label="Buttons" />}
                                {plan.aux_bite_ramps && <AuxChip label="Bite ramps" />}
                                {plan.aux_mandibular_advancement && <AuxChip label="Mandibular advancement" />}
                                {plan.aux_precision_cuts && <AuxChip label="Precision cuts" />}
                                {plan.aux_hooks && <AuxChip label="Hooks" />}
                            </div>
                            {plan.aux_other_notes && (
                                <p className="text-xs text-foreground/80 whitespace-pre-wrap">{plan.aux_other_notes}</p>
                            )}
                        </div>
                    )}

                    {/* A — Retention plan */}
                    {(plan.retainer_type || plan.retention_schedule || plan.retention_recall_months) && (
                        <div className="border-t border-border pt-3 space-y-1.5" data-testid="plan-readonly-retention">
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Retention plan</div>
                            {plan.retainer_type && (
                                <Para label="Retainer" value={retainerLabel(plan.retainer_type, plan.retainer_other_text)} />
                            )}
                            {plan.retention_schedule && <Para label="Wear schedule" value={plan.retention_schedule} />}
                            {plan.retention_recall_months && (
                                <Para label="Follow-up recall" value={`${plan.retention_recall_months} month${plan.retention_recall_months === 1 ? "" : "s"}`} />
                            )}
                        </div>
                    )}

                    {plan.updated_by_name && (
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 pt-2 border-t border-border">
                            <Clock size={11} /> Last updated by {plan.updated_by_name}
                        </div>
                    )}
        </div>
    );
}

function Metric({ label, value, suffix }) {
    return (
        <div className="bg-secondary/30 rounded-lg p-2 text-center">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
            <div className="text-lg font-bold leading-tight mt-0.5">{value}</div>
            <div className="text-[10px] text-muted-foreground">{suffix}</div>
        </div>
    );
}

function BiteRow({ label, initial, final }) {
    return (
        <div className="text-xs">
            <div className="text-muted-foreground">{label}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
                <span className="font-semibold">{initial ?? ","}</span>
                <ArrowsLeftRight size={11} className="text-muted-foreground" />
                <span className="font-semibold text-primary">{final ?? ","}</span>
                <span className="text-[10px] text-muted-foreground">mm</span>
            </div>
        </div>
    );
}

function Para({ label, value }) {
    return (
        <div className="text-xs">
            <span className="text-muted-foreground">{label}:</span> <span className="text-foreground/90">{value}</span>
        </div>
    );
}

function AuxChip({ label }) {
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
            {label}
        </span>
    );
}

const RETAINER_TYPE_LABELS = {
    vivera: "Clear (Vivera-style)",
    hawley: "Hawley",
    fixed_bonded: "Fixed bonded",
    dual: "Dual (clear + fixed)",
    other: "Other",
};

function retainerLabel(type, otherText) {
    if (type === "other") return otherText ? `Other — ${otherText}` : "Other";
    return RETAINER_TYPE_LABELS[type] || type;
}

function PlanHistoryDialog({ open, onClose, versions }) {
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="plan-history-dialog">
                <DialogHeader>
                    <DialogTitle>Plan version history</DialogTitle>
                    <DialogDescription>
                        Each snapshot is taken automatically when the case moves between planning ↔ doctor review ↔ approved.
                    </DialogDescription>
                </DialogHeader>
                {versions === null && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}
                {versions && versions.length === 0 && (
                    <div className="text-sm text-muted-foreground py-8 text-center">No snapshots yet, the first one is taken when the planner sends the case for doctor review.</div>
                )}
                {versions && versions.length > 0 && (
                    <ol className="space-y-3 mt-2">
                        {versions.map((v) => (
                            <li key={v.id} className="border border-border rounded-xl p-3 bg-card" data-testid={`plan-version-${v.version}`}>
                                <div className="flex items-center justify-between flex-wrap gap-1 mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">v{v.version}</span>
                                        <span className="text-xs font-semibold uppercase tracking-wide">{(v.reason || "").replace(/_/g, " ")}</span>
                                    </div>
                                    <span className="text-[11px] text-muted-foreground">
                                        {v.snapshotted_by_name} · {new Date(v.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <KV k="Upper" v={v.plan?.aligner_count_upper} />
                                    <KV k="Lower" v={v.plan?.aligner_count_lower} />
                                    <KV k="Weeks" v={v.plan?.estimated_duration_weeks} />
                                    <KV k="IPR (mm)" v={v.plan?.total_ipr_mm} />
                                </div>
                                {v.plan?.planner_notes && (
                                    <div className="text-[11px] text-muted-foreground mt-2 line-clamp-3 whitespace-pre-wrap">
                                        {v.plan.planner_notes}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </DialogContent>
        </Dialog>
    );
}

function KV({ k, v }) {
    return (
        <div className="bg-secondary/30 rounded p-1.5">
            <div className="text-[10px] text-muted-foreground uppercase">{k}</div>
            <div className="font-semibold">{v ?? ","}</div>
        </div>
    );
}

/* The patient-progress aligner-stage tracker now lives on the case
   detail page as its own card (`AlignerStageTracker.jsx`). Removing it
   from inside the plan card keeps the plan focused on what the COE
   *planned*, with execution tracking as a separate concern. */
